import { useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    fetchFormResponses,
    fetchFormResponseDetail,
    fetchFormResponseSummary,
    deleteFormResponse,
    fetchFormDetail,
} from "../lib/api-client";
import type {
    ResponseListItem,
    ResponseDetailItem,
    FieldSummary,
} from "../lib/api-client";

type ViewMode = "table" | "summary";

/**
 * Format field value for display - handles arrays, objects (like address), and primitives
 */
function formatFieldValue(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }
    if (Array.isArray(value)) {
        return value.map(v => formatFieldValue(v)).join(", ");
    }
    if (typeof value === "object") {
        // Check if it's an address-like object
        const obj = value as Record<string, unknown>;
        const addressFields = ["houseNumber", "village", "post", "district", "state", "country", "pincode"];
        const hasAddressFields = addressFields.some(field => field in obj);

        if (hasAddressFields) {
            // Format as readable address
            const parts: string[] = [];
            if (obj.houseNumber) parts.push(String(obj.houseNumber));
            if (obj.village) parts.push(String(obj.village));
            if (obj.post) parts.push(`Post: ${obj.post}`);
            if (obj.district) parts.push(String(obj.district));
            if (obj.state) parts.push(String(obj.state));
            if (obj.pincode) parts.push(String(obj.pincode));
            if (obj.country && obj.country !== "India") parts.push(String(obj.country));
            return parts.join(", ");
        }

        // For other objects, show key-value pairs
        return Object.entries(obj)
            .filter(([, v]) => v !== null && v !== undefined && v !== "")
            .map(([k, v]) => `${k}: ${String(v)}`)
            .join(", ");
    }
    return String(value);
}

function FormResponsesPage(): JSX.Element {
    const { formId } = useParams<{ formId: string }>();
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState<ViewMode>("table");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deletingResponseId, setDeletingResponseId] = useState<string | null>(null);
    const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

    const formQuery = useQuery({
        queryKey: ["form-detail", formId],
        queryFn: () => fetchFormDetail(formId!),
        enabled: Boolean(formId),
    });

    const responsesQuery = useQuery({
        queryKey: ["form-responses", formId, page, pageSize],
        queryFn: () => fetchFormResponses(formId!, { page, pageSize }),
        enabled: Boolean(formId) && viewMode === "table",
    });

    const summaryQuery = useQuery({
        queryKey: ["form-responses-summary", formId],
        queryFn: () => fetchFormResponseSummary(formId!),
        enabled: Boolean(formId) && viewMode === "summary",
    });

    const responseDetailQuery = useQuery({
        queryKey: ["form-response-detail", formId, selectedResponseId],
        queryFn: () => fetchFormResponseDetail(formId!, selectedResponseId!),
        enabled: Boolean(formId) && Boolean(selectedResponseId),
    });

    const deleteMutation = useMutation({
        mutationFn: async (responseId: string) => {
            return deleteFormResponse(formId!, responseId);
        },
        onSuccess() {
            void queryClient.invalidateQueries({ queryKey: ["form-responses", formId] });
            void queryClient.invalidateQueries({ queryKey: ["form-responses-summary", formId] });
            setShowDeleteDialog(false);
            setDeletingResponseId(null);
            setSelectedResponseId(null);
            setToast({ type: "success", message: "Response deleted successfully." });
        },
        onError(error: unknown) {
            setToast({
                type: "error",
                message: error instanceof Error ? error.message : "Failed to delete response.",
            });
        },
    });

    const form = formQuery.data?.form;
    const responses = responsesQuery.data?.responses ?? [];
    const total = responsesQuery.data?.total ?? 0;
    const hasMore = responsesQuery.data?.hasMore ?? false;
    const summary = summaryQuery.data;
    const responseDetail = responseDetailQuery.data?.response;

    // Clear toast after 3 seconds
    if (toast) {
        setTimeout(() => setToast(null), 3000);
    }

    const containerStyle: CSSProperties = {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "1.5rem",
    };

    const headerStyle: CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1.5rem",
    };

    const tabsStyle: CSSProperties = {
        display: "flex",
        gap: "0.5rem",
        marginBottom: "1.5rem",
    };

    const tabStyle = (active: boolean): CSSProperties => ({
        padding: "0.5rem 1rem",
        borderRadius: "var(--radius-base)",
        border: "1px solid var(--color-border)",
        background: active ? "var(--color-primary)" : "transparent",
        color: active ? "white" : "var(--color-text)",
        cursor: "pointer",
        fontWeight: 500,
        fontSize: "0.875rem",
        transition: "all 0.15s",
    });

    const tableStyle: CSSProperties = {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "0.875rem",
    };

    const thStyle: CSSProperties = {
        padding: "0.75rem 1rem",
        textAlign: "left",
        borderBottom: "2px solid var(--color-border)",
        fontWeight: 600,
        color: "var(--color-text-muted)",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    };

    const tdStyle: CSSProperties = {
        padding: "0.75rem 1rem",
        borderBottom: "1px solid var(--color-border)",
    };

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div>
                    <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.25rem" }}>
                        Responses
                    </h1>
                    <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem" }}>
                        {form?.title ?? "Loading..."} - {total} total responses
                    </p>
                </div>
            </div>

            {/* View Mode Tabs */}
            <div style={tabsStyle}>
                <button
                    type="button"
                    style={tabStyle(viewMode === "table")}
                    onClick={() => setViewMode("table")}
                >
                    📋 Responses
                </button>
                <button
                    type="button"
                    style={tabStyle(viewMode === "summary")}
                    onClick={() => setViewMode("summary")}
                >
                    📊 Summary
                </button>
            </div>

            {/* Table View */}
            {viewMode === "table" && (
                <div style={{ flex: 1, overflowY: "auto" }}>
                    {responsesQuery.isLoading ? (
                        <div style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>
                            Loading responses...
                        </div>
                    ) : responses.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>
                            <p style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>📭 No responses yet</p>
                            <p>Share your form to start collecting responses.</p>
                        </div>
                    ) : (
                        <>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Submitted</th>
                                        <th style={thStyle}>Preview</th>
                                        <th style={thStyle}>Location</th>
                                        <th style={thStyle}>Device</th>
                                        <th style={thStyle}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {responses.map((response) => (
                                        <tr
                                            key={response.id}
                                            style={{
                                                cursor: "pointer",
                                                background: selectedResponseId === response.id ? "var(--color-primary-soft)" : undefined,
                                            }}
                                            onClick={() => setSelectedResponseId(response.id)}
                                        >
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 500 }}>
                                                    {new Date(response.submittedAt).toLocaleDateString()}
                                                </div>
                                                <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                                                    {new Date(response.submittedAt).toLocaleTimeString()}
                                                </div>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {Object.entries(response.previewData).slice(0, 2).map(([key, value]) => (
                                                        <div key={key} style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                                                            <strong>{key}:</strong> {formatFieldValue(value).slice(0, 50)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                            <td style={tdStyle}>
                                                {response.geoCountry ? (
                                                    <span>{response.geoCountry}{response.geoRegion ? `, ${response.geoRegion}` : ""}</span>
                                                ) : (
                                                    <span style={{ color: "var(--color-text-muted)" }}>—</span>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                {response.deviceType ? (
                                                    <span style={{ textTransform: "capitalize" }}>{response.deviceType}</span>
                                                ) : (
                                                    <span style={{ color: "var(--color-text-muted)" }}>—</span>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                                    <button
                                                        type="button"
                                                        className="button ghost"
                                                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.85rem" }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedResponseId(response.id);
                                                        }}
                                                        title="View response"
                                                    >
                                                        👁️
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="button ghost"
                                                        style={{
                                                            padding: "0.25rem 0.5rem",
                                                            fontSize: "0.85rem",
                                                            color: "var(--color-critical)",
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeletingResponseId(response.id);
                                                            setShowDeleteDialog(true);
                                                        }}
                                                        title="Delete response"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem", marginTop: "1.5rem" }}>
                                <button
                                    type="button"
                                    className="button ghost"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >
                                    ← Previous
                                </button>
                                <span style={{ padding: "0.5rem 1rem", color: "var(--color-text-muted)" }}>
                                    Page {page} of {Math.ceil(total / pageSize)}
                                </span>
                                <button
                                    type="button"
                                    className="button ghost"
                                    onClick={() => setPage((p) => p + 1)}
                                    disabled={!hasMore}
                                >
                                    Next →
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Summary View */}
            {viewMode === "summary" && (
                <div style={{ flex: 1, overflowY: "auto" }}>
                    {summaryQuery.isLoading ? (
                        <div style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>
                            Loading summary...
                        </div>
                    ) : !summary || summary.totalResponses === 0 ? (
                        <div style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>
                            <p style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>📭 No responses to summarize</p>
                            <p>Collect some responses first.</p>
                        </div>
                    ) : (
                        <div style={{ display: "grid", gap: "1.5rem" }}>
                            <div style={{
                                background: "var(--color-primary-soft)",
                                padding: "1.5rem",
                                borderRadius: "var(--radius-base)",
                                textAlign: "center",
                            }}>
                                <div style={{ fontSize: "2.5rem", fontWeight: 700, color: "var(--color-primary)" }}>
                                    {summary.totalResponses}
                                </div>
                                <div style={{ color: "var(--color-text-muted)" }}>Total Responses</div>
                            </div>

                            {summary.summaries.map((fieldSummary) => (
                                <FieldSummaryCard key={fieldSummary.fieldId} summary={fieldSummary} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Response Detail Modal */}
            {selectedResponseId && responseDetail && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                    onClick={() => setSelectedResponseId(null)}
                >
                    <div
                        style={{
                            background: "var(--color-surface)",
                            borderRadius: "var(--radius-base)",
                            padding: "2rem",
                            maxWidth: "600px",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            boxShadow: "var(--shadow-lg)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                            <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>Response Details</h2>
                            <button
                                type="button"
                                className="button ghost"
                                onClick={() => setSelectedResponseId(null)}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: "1.5rem" }}>
                            <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
                                Submitted: {new Date(responseDetail.submittedAt).toLocaleString()}
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                                Device: {responseDetail.deviceType ?? "Unknown"} |
                                Location: {responseDetail.geoCountry ?? "Unknown"}
                            </div>
                        </div>

                        <div style={{ display: "grid", gap: "1rem" }}>
                            {Object.entries(responseDetail.payload).map(([fieldId, value]) => (
                                <div
                                    key={fieldId}
                                    style={{
                                        background: "var(--color-bg-solid)",
                                        padding: "1rem",
                                        borderRadius: "calc(var(--radius-base) - 2px)",
                                    }}
                                >
                                    <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>
                                        {fieldId}
                                    </div>
                                    <div style={{ fontWeight: 500 }}>
                                        {formatFieldValue(value)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className="button ghost"
                                onClick={() => {
                                    setDeletingResponseId(selectedResponseId);
                                    setShowDeleteDialog(true);
                                }}
                            >
                                🗑️ Delete
                            </button>
                            <button
                                type="button"
                                className="button primary"
                                onClick={() => setSelectedResponseId(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && deletingResponseId && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1001,
                    }}
                    onClick={() => setShowDeleteDialog(false)}
                >
                    <div
                        style={{
                            background: "var(--color-surface)",
                            borderRadius: "var(--radius-base)",
                            padding: "2rem",
                            maxWidth: "400px",
                            boxShadow: "var(--shadow-lg)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem" }}>
                            Delete Response?
                        </h2>
                        <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
                            This action cannot be undone. The response data will be permanently removed.
                        </p>
                        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className="button ghost"
                                onClick={() => setShowDeleteDialog(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="button"
                                style={{ background: "var(--color-critical)", color: "white" }}
                                onClick={() => deleteMutation.mutate(deletingResponseId)}
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "1.5rem",
                        right: "1.5rem",
                        background: toast.type === "success" ? "var(--color-success)" : "var(--color-critical)",
                        color: "white",
                        padding: "1rem 1.5rem",
                        borderRadius: "var(--radius-base)",
                        boxShadow: "var(--shadow-lg)",
                        zIndex: 1002,
                    }}
                >
                    {toast.message}
                </div>
            )}
        </div>
    );
}

function FieldSummaryCard({ summary }: { summary: FieldSummary }): JSX.Element {
    const cardStyle: CSSProperties = {
        background: "var(--color-bg-solid)",
        padding: "1.5rem",
        borderRadius: "var(--radius-base)",
        border: "1px solid var(--color-border)",
    };

    const barContainerStyle: CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        marginBottom: "0.5rem",
    };

    const barStyle = (percentage: number): CSSProperties => ({
        height: "1.5rem",
        width: `${Math.max(percentage, 5)}%`,
        background: "var(--color-primary)",
        borderRadius: "calc(var(--radius-base) - 2px)",
        transition: "width 0.3s ease",
    });

    return (
        <div style={cardStyle}>
            <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
                {summary.fieldTitle}
            </h3>

            {summary.data.type === "choice" && summary.data.options && (
                <div>
                    {summary.data.options.map((option) => (
                        <div key={option.label} style={barContainerStyle}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                                    <span style={{ fontSize: "0.875rem" }}>{option.label}</span>
                                    <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                                        {option.count} ({option.percentage}%)
                                    </span>
                                </div>
                                <div style={{ background: "var(--color-border)", borderRadius: "calc(var(--radius-base) - 2px)", height: "0.5rem" }}>
                                    <div style={{ ...barStyle(option.percentage), height: "0.5rem" }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {summary.data.type === "rating" && summary.data.distribution && (
                <div>
                    <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--color-primary)", marginBottom: "0.5rem" }}>
                        {summary.data.average?.toFixed(1)} ⭐
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        {summary.data.distribution.map((item) => (
                            <div key={item.value} style={{ textAlign: "center", flex: 1 }}>
                                <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                                    {item.value}
                                </div>
                                <div style={{ fontWeight: 500 }}>{item.count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {summary.data.type === "number" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", textAlign: "center" }}>
                    <div>
                        <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>Min</div>
                        <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>{summary.data.min}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>Average</div>
                        <div style={{ fontSize: "1.5rem", fontWeight: 600, color: "var(--color-primary)" }}>
                            {summary.data.average?.toFixed(1)}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>Max</div>
                        <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>{summary.data.max}</div>
                    </div>
                </div>
            )}

            {summary.data.type === "text" && summary.data.sampleResponses && (
                <div>
                    <div style={{ fontSize: "0.875rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
                        {summary.data.totalResponses} responses
                    </div>
                    <div style={{ display: "grid", gap: "0.5rem" }}>
                        {summary.data.sampleResponses.slice(0, 5).map((sample, index) => (
                            <div
                                key={index}
                                style={{
                                    padding: "0.75rem",
                                    background: "var(--color-surface)",
                                    borderRadius: "calc(var(--radius-base) - 2px)",
                                    fontSize: "0.875rem",
                                }}
                            >
                                "{sample}"
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default FormResponsesPage;
